import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { normalizeUAEPhone, isUAEPhoneValid } from "../utils/phone";
import useAddresses from "../hooks/useAddresses";
import PageHeader from "../components/ui/PageHeader";
import Avatar from "../components/ui/Avatar";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import AddressCard from "../components/AddressCard";
import AddressFormModal from "../components/AddressFormModal";

function CameraIcon() {
  return (
    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function StatItem({ value, label }) {
  return (
    <div className="flex flex-col items-center py-5 px-4">
      <p className="text-2xl font-bold text-ink-900 leading-none">{value}</p>
      <p className="text-xs text-ink-400 mt-1.5 text-center">{label}</p>
    </div>
  );
}

function formatMemberSince(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(null); // 'info' | null
  const [saved, setSaved] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const avatarInputRef = useRef(null);

  // ── Info form state ──────────────────────────────────────────────────────────
  const [infoForm, setInfoForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    bio: "",
  });
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [infoError, setInfoError] = useState(null);
  const phoneValid = isUAEPhoneValid(phoneDisplay);

  // ── Saved addresses ──────────────────────────────────────────────────────────
  const {
    addresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  } = useAddresses();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressActionError, setAddressActionError] = useState(null);
  const [deletingAddressId, setDeletingAddressId] = useState(null);

  // ── Shared saving state ──────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const base = process.env.REACT_APP_API_URL;

    Promise.all([
      axios.get(`${base}/api/auth/me`, { headers }),
      axios.get(`${base}/api/auth/stats`, { headers }),
    ])
      .then(([profileRes, statsRes]) => {
        setProfile(profileRes.data.user);
        setStats(statsRes.data.stats);
      })
      .catch((err) => setError(err.response?.data?.error || "Failed to load profile"))
      .finally(() => setLoading(false));
  }, []);

  const patchProfile = async (updates) => {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/api/auth/me`,
      updates,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return res.data.user;
  };

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAvatarUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem("token");
      const uploadRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload/listing-photo`,
        { base64, fileType: file.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = await patchProfile({ avatar_url: uploadRes.data.url });
      setProfile(updated);
      flashSaved();
    } catch {
      setAvatarError('Photo upload failed. Please try again.');
      setTimeout(() => setAvatarError(null), 4000);
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const openModal = (type) => {
    if (type === "info") {
      setInfoForm({
        first_name: profile.first_name ?? "",
        last_name: profile.last_name ?? "",
        phone: profile.phone ?? "",
        bio: profile.bio ?? "",
      });
      setPhoneDisplay(
        profile.phone ? profile.phone.replace("+971", "") : ""
      );
      setInfoError(null);
    }
    setEditModal(type);
  };

  const handlePhone = (e) => {
    const { display, stored } = normalizeUAEPhone(e.target.value);
    setPhoneDisplay(display);
    setInfoForm((prev) => ({ ...prev, phone: stored }));
    setInfoError(null);
  };

  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setInfoForm((f) => ({ ...f, [name]: value }));
    setInfoError(null);
  };

  const handleSave = async (type) => {
    if (type === "info" && !phoneValid) {
      setInfoError("Enter a valid 9-digit UAE number");
      return;
    }
    setSaving(true);
    try {
      const updated = await patchProfile(infoForm);
      setProfile(updated);
      setEditModal(null);
      flashSaved();
    } catch (err) {
      setInfoError(err.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const openAddAddress = () => {
    setEditingAddress(null);
    setAddressActionError(null);
    setAddressModalOpen(true);
  };

  const openEditAddress = (address) => {
    setEditingAddress(address);
    setAddressActionError(null);
    setAddressModalOpen(true);
  };

  const handleSetDefaultAddress = async (id) => {
    try {
      await setDefaultAddress(id);
    } catch (err) {
      setAddressActionError(err.response?.data?.error || "Failed to update default address");
    }
  };

  const handleDeleteAddress = async (id) => {
    setDeletingAddressId(id);
    try {
      await deleteAddress(id);
    } catch (err) {
      setAddressActionError(err.response?.data?.error || "Failed to delete address");
    } finally {
      setDeletingAddressId(null);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div
          role="status"
          aria-label="Loading"
          className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin"
        />
      </div>
    );

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error)
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg border border-uae-red/20 px-6 py-8 text-center max-w-sm w-full shadow-card">
          <p className="text-sm font-medium text-uae-red mb-1">
            Something went wrong
          </p>
          <p className="text-xs text-ink-400">{error}</p>
        </div>
      </div>
    );

  // ── Empty ────────────────────────────────────────────────────────────────────
  if (!profile)
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <p className="text-xs text-ink-400">No profile found.</p>
      </div>
    );

  const isCustomer = profile?.role === "customer";

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        <PageHeader title="My Profile" />

        {/* Identity card */}
        <div className="bg-white border border-sand-200 rounded-lg shadow-card mb-4">
          <div className="p-6 flex items-center gap-5">
            {/* Avatar with upload overlay */}
            <div className="relative shrink-0 w-20 h-20 group">
              <Avatar
                src={profile?.avatar_url}
                name={`${profile?.first_name} ${profile?.last_name}`}
                size="xl"
              />

              {/* Camera overlay — visible on hover or while uploading */}
              <label
                className={[
                  "absolute inset-0 rounded-full flex items-center justify-center cursor-pointer transition-opacity duration-200",
                  avatarUploading
                    ? "bg-ink-900/60 opacity-100"
                    : "bg-ink-900/40 opacity-0 group-hover:opacity-100",
                ].join(" ")}
                title="Change photo"
              >
                {avatarUploading ? (
                  <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <CameraIcon />
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                />
              </label>
            </div>

            <div>
              <p className="font-heading text-xl font-semibold text-ink-900">
                {profile?.first_name} {profile?.last_name}
              </p>
              <p className="text-sm text-ink-400 mt-0.5">{profile?.email}</p>
              <Badge variant="category" className="mt-2 capitalize">
                {profile?.role}
              </Badge>
            </div>
          </div>

          {/* Stats row */}
          {stats && (
            <div className={`border-t border-sand-100 grid ${isCustomer ? 'grid-cols-2' : 'grid-cols-3'} divide-x divide-sand-100`}>
              <StatItem
                value={formatMemberSince(profile?.created_at)}
                label="Member since"
              />
              {isCustomer ? (
                <StatItem value={stats.orders_placed ?? 0} label="Orders placed" />
              ) : (
                <>
                  <StatItem value={stats.listings_count ?? 0} label="Listings" />
                  <StatItem value={stats.orders_received ?? 0} label="Orders received" />
                </>
              )}
            </div>
          )}
        </div>

        {/* Info section */}
        <div className="bg-white border border-sand-200 rounded-lg shadow-card mb-4">
          <div className="flex items-center justify-between px-6 py-4 border-b border-sand-100">
            <p className="font-heading text-base font-semibold text-ink-900">
              Personal Info
            </p>
            <Button variant="ghost" size="sm" onClick={() => openModal("info")}>
              Edit
            </Button>
          </div>
          <div className="px-6 py-4 flex flex-col gap-3">
            <div>
              <p className="text-xs text-ink-400 mb-0.5">Phone</p>
              <p className="text-sm text-ink-900">{profile?.phone || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-ink-400 mb-0.5">Bio</p>
              <p className="text-sm text-ink-900 leading-relaxed">
                {profile?.bio || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Saved Addresses section */}
        {isCustomer && (
          <div className="bg-white border border-sand-200 rounded-lg shadow-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-sand-100">
              <p className="font-heading text-base font-semibold text-ink-900">
                Saved Addresses
              </p>
              <Button variant="ghost" size="sm" onClick={openAddAddress}>
                <Plus className="w-3.5 h-3.5" /> Add new
              </Button>
            </div>
            <div className="px-6 py-4 flex flex-col gap-3">
              {addresses.length === 0 && (
                <p className="text-sm text-ink-400">No saved addresses yet.</p>
              )}
              {addresses.map((address) => (
                <AddressCard
                  key={address.id}
                  address={address}
                  onEdit={() => openEditAddress(address)}
                  onSetDefault={() => handleSetDefaultAddress(address.id)}
                  onDelete={() => handleDeleteAddress(address.id)}
                  busy={deletingAddressId === address.id}
                />
              ))}
              {addressActionError && (
                <p className="text-xs text-uae-red">{addressActionError}</p>
              )}
            </div>
          </div>
        )}

        {/* Toasts */}
        {saved && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink-900 text-white text-sm px-5 py-3 rounded-lg shadow-modal z-50">
            Profile saved
          </div>
        )}
        {avatarError && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-uae-red text-white text-sm px-5 py-3 rounded-lg shadow-modal z-50">
            {avatarError}
          </div>
        )}

        {/* Edit Info Modal */}
        <Modal
          isOpen={editModal === "info"}
          onClose={() => !saving && setEditModal(null)}
          title="Edit Personal Info"
          footer={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditModal(null)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={saving}
                onClick={() => handleSave("info")}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              <Input
                label="First name"
                id="first_name"
                name="first_name"
                value={infoForm.first_name}
                onChange={handleInfoChange}
                disabled={saving}
                className="flex-1"
              />
              <Input
                label="Last name"
                id="last_name"
                name="last_name"
                value={infoForm.last_name}
                onChange={handleInfoChange}
                disabled={saving}
                className="flex-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-ink-700">Phone</label>
              <div
                className={[
                  "flex items-center border rounded-md overflow-hidden transition-all duration-200",
                  "focus-within:ring-2 focus-within:ring-terra-400 focus-within:border-terra-400",
                  !phoneValid && phoneDisplay.length > 0
                    ? "border-uae-red"
                    : "border-sand-200",
                ].join(" ")}
              >
                <span className="flex items-center gap-1.5 px-3 py-2.5 bg-sand-50 border-r border-sand-200 text-sm text-ink-400 shrink-0 select-none whitespace-nowrap">
                  +971
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="5X-XXX-XXXX"
                  className="flex-1 px-3 py-2.5 text-sm border-none focus:outline-none bg-white disabled:opacity-50 text-ink-900"
                  value={phoneDisplay}
                  onChange={handlePhone}
                  maxLength={9}
                  disabled={saving}
                />
              </div>
              {!phoneValid && phoneDisplay.length > 0 && (
                <p className="text-xs text-uae-red">
                  Enter a valid 9-digit UAE number
                </p>
              )}
            </div>

            <Input
              label="Bio"
              id="bio"
              as="textarea"
              name="bio"
              value={infoForm.bio}
              onChange={handleInfoChange}
              rows={3}
              disabled={saving}
              placeholder="Tell us a bit about yourself…"
              className="resize-none"
            />

            {infoError && (
              <p className="text-xs text-uae-red">{infoError}</p>
            )}
          </div>
        </Modal>

        {/* Add/Edit Address Modal */}
        <AddressFormModal
          isOpen={addressModalOpen}
          onClose={() => setAddressModalOpen(false)}
          address={editingAddress}
          forceDefault={addresses.length === 0 && !editingAddress}
          createAddress={createAddress}
          updateAddress={updateAddress}
        />

      </div>
    </div>
  );
}
